import { AUTH_PAGES, registerSchema, routes } from '@pmai/contracts';
import { Alert, Button, TextField } from '@pmai/ui';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useApiMutation } from '../../shared/api/hooks';
import { applyFieldErrors, bind, useZodForm } from '../../shared/forms/form';
import { useErrorText } from '../../shared/i18n';
import { AuthCard, BackToLogin, NewPasswordField } from './auth-layout';

/** FR-AUTH-101: account + new workspace; the answer never reveals whether the e-mail is taken. */
export function RegisterPage() {
  const { t } = useTranslation();
  const errorText = useErrorText();
  const form = useZodForm(registerSchema, {
    defaultValues: { displayName: '', workspaceName: '', email: '', password: '' },
  });
  const register = useApiMutation(routes.auth.register, {
    onError: (error) => applyFieldErrors(form, error),
  });

  if (register.isSuccess) {
    return (
      <AuthCard title={t('auth.register.doneTitle')} footer={<BackToLogin />}>
        <Alert tone="success">{t('auth.register.doneText', { email: form.getValues('email') })}</Alert>
      </AuthCard>
    );
  }

  return (
    <AuthCard
      title={t('auth.register.title')}
      footer={
        <span>
          {t('auth.register.haveAccount')}{' '}
          <Link to={AUTH_PAGES.login} className="text-brand-700 hover:underline">
            {t('auth.login.submit')}
          </Link>
        </span>
      }
    >
      <form
        noValidate
        className="flex flex-col gap-4"
        onSubmit={form.handleSubmit((body) => register.mutate({ body }))}
      >
        {register.error && <Alert tone="danger">{errorText(register.error)}</Alert>}
        <TextField
          label={t('auth.register.displayName')}
          autoComplete="name"
          required
          {...bind(form, 'displayName')}
        />
        <TextField
          label={t('auth.register.workspaceName')}
          hint={t('auth.register.workspaceHint')}
          autoComplete="organization"
          required
          {...bind(form, 'workspaceName')}
        />
        <TextField
          label={t('auth.email')}
          type="email"
          autoComplete="email"
          required
          {...bind(form, 'email')}
        />
        <NewPasswordField {...bind(form, 'password')} />
        <Button type="submit" loading={register.isPending}>
          {t('auth.register.submit')}
        </Button>
      </form>
    </AuthCard>
  );
}
